/**
 * Hook for detecting new users and their onboarding state
 * Provides contextual information for enhanced UX
 */

import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface NewUserState {
  isNewUser: boolean;
  daysSinceRegistration: number;
  hasZeroBalance: boolean;
  hasNoTransactions: boolean;
  needsOnboarding: boolean;
  registrationDate: Date | null;
}

export const useNewUserDetection = (): NewUserState => {
  const { user } = useAuth();

  const newUserState = useMemo((): NewUserState => {
    if (!user) {
      return {
        isNewUser: false,
        daysSinceRegistration: 0,
        hasZeroBalance: true,
        hasNoTransactions: true,
        needsOnboarding: false,
        registrationDate: null,
      };
    }

    const registrationDate = user.createdAt ? new Date(user.createdAt) : new Date();
    const now = new Date();
    const daysSinceRegistration = Math.floor(
      (now.getTime() - registrationDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    const isNewUser = daysSinceRegistration <= 7; // New user for 7 days
    const hasZeroBalance = (user.balance || 0) === 0;
    const needsOnboarding = isNewUser && hasZeroBalance;

    return {
      isNewUser,
      daysSinceRegistration,
      hasZeroBalance,
      hasNoTransactions: true, // Will be updated when we have transaction data
      needsOnboarding,
      registrationDate,
    };
  }, [user]);

  return newUserState;
};